import styled from '@emotion/styled';
import { Text } from '@template-project/afrodita-components';
import { Link } from 'react-router-dom';

/* ------------------------------------------------------------------ */
/*  Header bar                                                         */
/* ------------------------------------------------------------------ */

export const HeaderContainer = styled.header`
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space['2xl']};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  }
`;

export const Logo = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
`;

export const LogoText = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[7]}px;
  letter-spacing: -0.02em;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: ${({ theme }) => theme.fontSizes[5]}px;
  }
`;

/* ------------------------------------------------------------------ */
/*  Desktop nav                                                        */
/* ------------------------------------------------------------------ */

export const DesktopNav = styled.nav`
  display: flex;
  gap: ${({ theme }) => theme.space.xl};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: none;
  }
`;

export const NavLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  transition: color 0.2s;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const SaleLink = styled(NavLink)`
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.urgent.color};
    opacity: 0.8;
  }
`;

/* ------------------------------------------------------------------ */
/*  Mobile header layout                                               */
/* ------------------------------------------------------------------ */

export const MobileLeft = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: flex;
    align-items: center;
  }
`;

export const MobileRight = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.space.xs};
  }
`;

/* ------------------------------------------------------------------ */
/*  Shared buttons                                                     */
/* ------------------------------------------------------------------ */

export const IconButton = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.space.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

export const CartBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.semantic.urgent.color};
  color: ${({ theme }) => theme.colors.shades.white.color};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  min-width: 16px;
  height: 16px;
  border-radius: ${({ theme }) => theme.radius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

/* ------------------------------------------------------------------ */
/*  Mobile drawer                                                      */
/* ------------------------------------------------------------------ */

export const DrawerCloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

export const SearchShortcut = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  width: 100%;
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.background.disabled};
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

export const DrawerNav = styled.nav`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.space.lg};
  gap: ${({ theme }) => theme.space.md};
`;

export const DrawerNavLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  padding: ${({ theme }) => theme.space.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const DrawerSaleLink = styled(DrawerNavLink)`
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.urgent.color};
    opacity: 0.8;
  }
`;

export const DrawerSection = styled.div`
  padding: ${({ theme }) => theme.space.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;
